import { AppModuleCode, PermissionCode, type AuthUser } from '@gb-ticket/shared-types';
import { useLocation } from 'react-router';

import { hasPermission, isModuleEnabled } from '@/features/auth/permissions';
import { HELPDESK_ROOT, stripHelpdeskPrefix } from '@/products/helpdesk/helpdesk-paths';

export const PORTAL_ROOT = '/portal';
export const FAQ_CATEGORY_SLUG = 'faq';

export function isPortalAudience(user: AuthUser | null | undefined): boolean {
  return Boolean(user) && !hasPermission(user, PermissionCode.TICKETS_READ_ALL);
}

export function isPortalPath(pathname: string): boolean {
  return pathname === PORTAL_ROOT || pathname.startsWith(`${PORTAL_ROOT}/`);
}

export type PortalNavItem = {
  to: string;
  label: string;
  end?: boolean;
};

export function portalNavItems(user: AuthUser): PortalNavItem[] {
  const showKnowledge =
    hasPermission(user, PermissionCode.KB_READ) && isModuleEnabled(user, AppModuleCode.KNOWLEDGE);
  return [
    { to: PORTAL_ROOT, label: 'Start', end: true },
    { to: `${PORTAL_ROOT}/tickets`, label: 'Meine Anfragen' },
    ...(showKnowledge
      ? [
          { to: `${PORTAL_ROOT}/wissen`, label: 'Hilfe finden' },
          { to: `${PORTAL_ROOT}/faq`, label: 'Häufige Fragen' },
        ]
      : []),
  ];
}

export function defaultHomePath(user: AuthUser): string {
  return isPortalAudience(user) ? PORTAL_ROOT : '/start';
}

/** Map an agent-UI path to the Self-Service equivalent, or null if it should stay. */
export function agentPathToPortal(pathWithSearch: string): string | null {
  const raw = pathWithSearch.startsWith('/') ? pathWithSearch : `/${pathWithSearch}`;
  const qIndex = raw.indexOf('?');
  const pathname = stripHelpdeskPrefix(qIndex >= 0 ? raw.slice(0, qIndex) : raw);
  const query = qIndex >= 0 ? raw.slice(qIndex) : '';
  if (isPortalPath(pathname)) {
    return null;
  }
  if (pathname === '/') {
    return PORTAL_ROOT;
  }
  if (pathname === '/start') {
    return PORTAL_ROOT;
  }
  if (pathname === '/einstellungen') {
    return `${PORTAL_ROOT}/einstellungen${query}`;
  }
  if (pathname.startsWith('/tickets') || pathname.startsWith('/wissen')) {
    return `${PORTAL_ROOT}${pathname}${query}`;
  }
  if (pathname.startsWith('/changes')) {
    return PORTAL_ROOT;
  }
  return null;
}

export function resolvePostLoginPath(user: AuthUser, from: string): string {
  const target = from.trim() || '/';
  if (target === '/anmelden') {
    return defaultHomePath(user);
  }
  if (isPortalAudience(user)) {
    if (isPortalPath(target.split('?')[0] ?? target)) {
      return target;
    }
    return agentPathToPortal(target) ?? defaultHomePath(user);
  }
  // Agents: bare `/` lands on Mitarbeiterportal hub (`/start`); deep links stay.
  if (target === '/') {
    return defaultHomePath(user);
  }
  return target;
}

export function useAppPaths() {
  const { pathname } = useLocation();
  const portal = isPortalPath(pathname);
  const prefix = portal ? PORTAL_ROOT : HELPDESK_ROOT;
  return {
    isPortal: portal,
    home: prefix,
    tickets: `${prefix}/tickets`,
    ticketNew: `${prefix}/tickets/neu`,
    ticket: (ref: string | number) => `${prefix}/tickets/${ref}`,
    ticketHistory: (ref: string | number) => `${prefix}/tickets/${ref}/historie`,
    wissen: `${prefix}/wissen`,
    article: (slug: string) => `${prefix}/wissen/${slug}`,
    faq: `${PORTAL_ROOT}/faq`,
    settings: portal ? `${PORTAL_ROOT}/einstellungen` : '/einstellungen',
  };
}
